import { useEffect, useState } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Button from "@src/components/Button/Button";
import Hint from "@src/components/Helpers/Hint/Hint";
import "../styles/form.sass";
import VersionForm from "./VersionForm";
import CharacterMetaForm from "./CharacterMetaForm";
import { CharacterDTO, Version } from "@src/types/global";

const schema = yup.object().shape({
    is_public: yup.boolean().default(false).required(),
    first_name: yup
        .string()
        .min(1)
        .max(250)
        .required({ msg: "Please provide a first name." }),
    last_name: yup
        .string()
        .min(1)
        .max(250)
        .required({ msg: "Please provide a last name." }),
    avatar: yup.mixed(),
    version_name: yup.string().min(1).max(250).default("Default").required(),
    is_default: yup.boolean().required(),
    is_nsfw: yup.boolean().required(),
    age: yup.string().min(0).required(),
    ears: yup.string().min(0).max(250),
    eyes: yup.string().min(0).max(250),
    faceshape: yup.string().max(250),
    mouth: yup.string().max(250),
    nose: yup.string().max(250),
    about: yup.string().max(500),
    body: yup.mixed().required(),
    height: yup.number().required(),
    override_height: yup
        .number()
        .transform((_, value) => {
            return value == Number(value) ? Number(value) : null;
        })
        .nullable()
        .label("Override height"),
    pronouns: yup
        .string()
        .min(1)
        .max(125)
        .required({ msg: "Please provide pronouns (or lackthereof)" }),
    flaws: yup.array(),
    qualities: yup.array(),
});
interface FormValues extends yup.InferType<typeof schema> {}
const initiaState = {
    is_public: false,
    version_name: "Default",
    is_default: true,
    is_nsfw: false,
} as FormValues;

interface IProps {
    submitFormFn: ({
        characterDTO,
        versionDTO,
        bodyForm,
        avatarForm,
        dirtyFields,
    }: {
        characterDTO: CharacterDTO;
        versionDTO: Version;
        bodyForm: FormData;
        avatarForm: FormData;
        dirtyFields?: { [x: string]: boolean | any[] | {} };
    }) => Promise<void>;
    character?: FormValues;
    measurementUnit: "metric" | "imperial";
}
const CharacterForm = ({
    submitFormFn,
    character = initiaState,
    measurementUnit,
}: IProps) => {
    const [loading, setLoading] = useState(false);
    const [hints, setHints] = useState(true);

    const methods = useForm({
        defaultValues: character,
        resolver: yupResolver(schema),
    });
    const {
        handleSubmit,
        setValue,
        formState: { dirtyFields },
    } = methods;
    /**
     * Displays hint boxes
     */
    function showHints(
        e:
            | React.MouseEvent<HTMLButtonElement>
            | React.KeyboardEvent<HTMLButtonElement>,
    ) {
        const target = e.target as HTMLButtonElement;
        if (target.value == "true") {
            setHints(true);
        } else {
            setHints(false);
        }
    }
    useEffect(() => {
        if (character.body) {
            setValue("body", character.body);
        }
        if (character.avatar) {
            setValue("avatar", character.avatar);
        }
    }, []);
    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        setLoading(true);

        // Split elements
        const { body, avatar, ...character } = data;
        const { last_name, first_name, is_public, ...untypedVersion } =
            character;
        const version = untypedVersion as any as Version;

        untypedVersion.qualities !== undefined
            ? (version.qualities = untypedVersion.qualities.join(","))
            : (version.qualities = "");
        untypedVersion.flaws !== undefined
            ? (version.flaws = untypedVersion.flaws.join(","))
            : (version.flaws = "");

        const characterDTO = {
            last_name,
            first_name,
            is_public,
        };

        const versionDTO = {
            ...version,
        };
        const bodyForm = new FormData();
        //@ts-expect-error We use mixed() in our yup schema definition
        bodyForm.append("body", body);
        const avatarForm = new FormData();
        avatarForm.append("avatar", avatar);

        await submitFormFn({
            characterDTO,
            versionDTO,
            bodyForm,
            avatarForm,
            dirtyFields,
        });

        setLoading(false);
    };
    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Hint title="Need some inspiration ?">
                    <span>
                        If you need it, these boxes will provide examples or
                        guidelines as to what you can write here in each
                        section.
                        <div className="hint__buttons">
                            <Button
                                preset="stealth"
                                type="button"
                                value="false"
                                fn={showHints}
                            >
                                No thank you!
                            </Button>

                            <Button
                                preset="stealth"
                                type="button"
                                value="true"
                                fn={showHints}
                            >
                                Yes please!
                            </Button>
                        </div>
                    </span>
                </Hint>
                <CharacterMetaForm avatar={character.avatar} />
                <VersionForm
                    hints={hints}
                    //@ts-ignore
                    bodyImage={character.body ? character.body : ""}
                    measurementUnit={measurementUnit}
                />
                <div className="form__group">
                    <p className="info">
                        Done with the basics ! <br /> Go to the character page
                        to fill in expressions, outfits or scene presets.
                    </p>
                    <Button type="submit" preset="primary" isLoading={loading}>
                        Submit
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};
export default CharacterForm;
